/**
 * 腾讯云 COS 文件上传 Composable (Vue3)
 * 主要功能：
 * 1. 文件上传到腾讯云对象存储
 * 2. 支持大文件分片上传
 * 3. 文件 MD5 计算和校验
 * 4. 上传进度监控
 * 5. 文件名处理和格式验证
 */

import { ref, reactive, computed, nextTick, readonly } from 'vue'
import COS from 'cos-js-sdk-v5'
import SparkMD5 from 'spark-md5'
import { getCOSToken } from '@views/app.api'

// 常量定义
const CONSTANTS = {
  // 大文件阈值 (80MB)
  LARGE_FILE_THRESHOLD: 80 * 1024 * 1024,
  // 分片上传阈值 (10MB)
  SLICE_UPLOAD_THRESHOLD: 10 * 1024 * 1024,
  // MD5 分片数量
  MD5_SLICE_COUNT: 15,
  BUCKET: 'geoproject-1253383455', // 'geoproject-1253383455', // 'spmf-1253383455',
  REGION: 'ap-shanghai',
  // 支持的文件类型
  SUPPORTED_FILE_TYPES: [
    'doc',
    'ppt',
    'pdf',
    'docx',
    'pptx',
    'mp4',
    'avi',
    'rmvb',
    'mov',
    'xls',
    'xlsx',
    '7z',
    'zip',
    'rar',
    'jpg',
    'jpeg',
    'gif',
    'png',
    'bmp',
    'mp3',
    'wav',
    'aac',
  ],
}

/**
 * COS 文件上传 Composable
 * @returns {Object} 上传相关的方法和状态
 */
export default function useCosUpload() {
  // 响应式状态
  const cosManager = ref(null)
  const uploadState = reactive({
    isUploading: false,
    progress: 0,
    error: null,
    taskId: '',
  })

  // 计算属性
  const canUpload = computed(() => !uploadState.isUploading)
  const hasError = computed(() => !!uploadState.error)
  const isCompleted = computed(() => uploadState.progress === 100)

  /**
   * 重置上传状态
   */
  const resetUploadState = () => {
    uploadState.isUploading = false
    uploadState.progress = 0
    uploadState.error = null
    uploadState.taskId = ''
  }

  /**
   * 设置错误状态
   * @param {string|Error} error - 错误信息
   */
  const setError = error => {
    uploadState.error = error instanceof Error ? error.message : error
    uploadState.isUploading = false
  }

  /**
   * 大文件 MD5 计算（分片处理）
   * @param {File} file - 文件对象
   * @returns {Promise<string>} MD5 值
   */
  const calculateLargeFileMD5 = file => {
    return new Promise((resolve, reject) => {
      const chunkSize = Math.ceil(file.size / CONSTANTS.MD5_SLICE_COUNT)
      const fileReader = new FileReader()
      const md5 = new SparkMD5()
      let currentIndex = 0
      const loadNextChunk = () => {
        const slice = file.slice(currentIndex, currentIndex + chunkSize)
        fileReader.readAsBinaryString(slice)
      }

      fileReader.onload = event => {
        md5.appendBinary(event.target.result)

        if (currentIndex < file.size) {
          currentIndex += chunkSize
          loadNextChunk()
        } else {
          resolve(md5.end())
        }
      }

      fileReader.onerror = () => {
        const error = new Error('大文件 MD5 计算失败')
        console.error('大文件 MD5 计算失败:', error)
        reject(error)
      }

      loadNextChunk()
    })
  }

  /**
   * 小文件 MD5 计算
   * @param {File} file - 文件对象
   * @returns {Promise<string>} MD5 值
   */
  const calculateSmallFileMD5 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = event => {
        try {
          const spark = new SparkMD5.ArrayBuffer()
          spark.append(event.target.result)
          const md5 = spark.end()
          resolve(md5 || '')
        } catch (error) {
          console.error('小文件 MD5 计算失败:', error)
          reject(new Error('小文件 MD5 计算失败'))
        }
      }

      reader.onerror = () => {
        const error = new Error('文件读取失败')
        console.error('文件读取失败:', error)
        reject(error)
      }

      reader.readAsArrayBuffer(file)
    })
  }

  /**
   * 计算文件 MD5 值
   * @param {File} file - 文件对象
   * @returns {Promise<string>} MD5 值
   */
  const calculateFileMD5 = async file => {
    try {
      if (file.size >= CONSTANTS.LARGE_FILE_THRESHOLD) {
        return await calculateLargeFileMD5(file)
      }
      return await calculateSmallFileMD5(file)
    } catch (error) {
      console.error('MD5 计算失败:', error)
      throw new Error('文件 MD5 计算失败')
    }
  }

  /**
   * 获取文件扩展名
   * @param {File} file - 文件对象
   * @returns {string} 文件扩展名
   */
  const getFileExtension = file => {
    return file?.name?.split('.').pop()
  }

  /**
   * 初始化腾讯云 COS 实例
   * @returns {Promise<Object>} COS 实例和凭证数据
   */
  const initCOS = async () => {
    try {
      const { data } = await getCOSToken()
      const tokenData = data?.credentials
      if (!tokenData) {
        throw new Error('获取 COS 凭证失败')
      }
      const cosInstance = new COS({
        getAuthorization(authOptions, callback) {
          callback({
            TmpSecretId: tokenData.tmpSecretId,
            TmpSecretKey: tokenData.tmpSecretKey,
            SecurityToken: tokenData.sessionToken,
            StartTime: data.startTime,
            ExpiredTime: data.expiredTime,
          })
        },
      })

      return { cosInstance, tokenData }
    } catch (error) {
      throw new Error('COS 初始化失败')
    }
  }

  /**
   * 创建上传任务配置
   * @param {File} file - 文件对象
   * @param {string} md5Id - MD5 ID
   * @returns {Object} 上传配置
   */
  const createUploadConfig = (file, md5Id, callback) => {
    const time = new Date().getTime()
    return {
      Bucket: CONSTANTS.BUCKET,
      Region: CONSTANTS.REGION,
      Key: `${time}_${md5Id}`,
      Body: file, // 避免直接修改原文件对象
      SliceSize: CONSTANTS.SLICE_UPLOAD_THRESHOLD,

      onTaskReady: taskId => {
        console.log('上传任务就绪:', { taskId, fileName: file.name })
        uploadState.taskId = taskId
        if (callback) {
          callback({ event: 'taskReady', message: { taskId } })
        }
      },

      onProgress: progressData => {
        console.log('上传进度:', progressData)
        uploadState.progress = Math.round(progressData.percent * 100)
        if (callback) {
          callback({ event: 'progress', message: progressData })
        }
      },

      onFileFinish: (err, data, fileOptions) => {
        const status = err ? '失败' : '完成'
        console.log(`${fileOptions.Key} 上传${status}`, { err, data })
        if (callback) {
          callback({ event: err ? 'error' : 'success', message: { err, data, fileOptions } })
        }
      },
    }
  }

  /**
   * 执行文件上传
   * @param {string} uploadId - 上传 ID
   * @param {string} md5Id - 文件 MD5 ID
   * @param {File} file - 要上传的文件对象
   * @param {Object} appendInfo - 附加信息
   * @returns {Promise} 上传结果
   */
  const executeFileUpload = async (uploadId, md5Id, file, appendInfo, callback) => {
    try {
      uploadState.isUploading = true

      // 初始化 COS 管理器
      const { cosInstance, tokenData } = await initCOS()
      cosManager.value = cosInstance

      // 检查文件是否已存在
      if (tokenData.filePath) {
        uploadState.progress = 100
        return {
          isExit: true,
          data: tokenData,
          md5IdCheck: md5Id,
          uploadIdCheck: uploadId,
          createTime: tokenData.createTime,
        }
      }

      // 执行文件上传
      return new Promise((resolve, reject) => {
        const cosConfig = createUploadConfig(file, md5Id, callback)

        cosManager.value.uploadFile(cosConfig, (err, data) => {
          const result = {
            err,
            data,
            md5IdCheck: md5Id,
            uploadIdCheck: uploadId,
            createTime: tokenData.createTime,
            cosConfig,
          }

          if (err) {
            console.error('文件上传失败:', err)
            setError('文件上传失败')
            reject(new Error('文件上传失败'))
          } else {
            uploadState.progress = 100
            resolve(result)
          }
        })
      })
    } catch (error) {
      console.error('上传过程出错:', error)
      setError(error.message)

      if (error.name === 'cancel') {
        throw error // 重新抛出取消错误
      }

      throw new Error('文件上传失败')
    }
  }

  /**
   * 创建文件信息对象
   * @param {File} file - 文件对象
   * @param {string} md5 - 文件 MD5
   * @param {string} extension - 文件扩展名
   * @param {Object} appendInfo - 附加信息
   * @returns {Object} 文件信息对象
   */
  const createFileInfo = (file, md5, appendInfo) => {
    const currentTime = Date.now()

    return {
      upload_id: `${currentTime}_${md5}`,
      id: md5,
      targetFile: file,
      fileName: file.name,
      fileSize: file.size,
      fileFormat: file?.name?.split('.').pop(),
      appendInfo,
      // progress: 0,
      // status: 'waiting',
      // cosTaskId: ''
      // uploadTime: currentTime,
      // finishTime: currentTime,
    }
  }

  /**
   * 文件选择和上传主入口
   * @param {File} file - 选择的文件
   * @param {Object} appendInfo - 附加信息
   * @param {Function} callback - 回调函数
   * @returns {Promise<Object>} 上传结果
   */
  const selectAndUploadFile = async (file, appendInfo = {}, callback = null) => {
    try {
      // 重置状态
      resetUploadState()

      // 验证文件
      if (!file || !file.name) {
        throw new Error('无效的文件对象')
      }

      // 计算文件 MD5
      const md5 = await calculateFileMD5(file)

      // 创建文件信息对象
      const fileInfo = createFileInfo(file, md5, appendInfo)

      // 执行文件上传
      const uploadResult = await executeFileUpload(
        fileInfo.upload_id,
        fileInfo.id,
        file,
        appendInfo,
        data => {
          if (callback) {
            callback({ data })
          }
        },
      )

      console.log('文件上传结果:', uploadResult)

      uploadState.isUploading = false
      const { cosConfig } = uploadResult
      return {
        success: true,
        filePath: cosConfig?.Key,
        fileFormat: fileInfo.fileFormat,
      }
    } catch (error) {
      console.error('文件选择和上传失败:', error)
      setError(error.message)

      return {
        success: false,
        error: error.message,
      }
    }
  }

  /**
   * 取消上传任务
   * @param {string} cosTaskId - COS 任务 ID
   * @returns {Promise<boolean>} 取消结果
   */
  const cancelUpload = async cosTaskId => {
    cosManager.value.cancelTask(cosTaskId)

    // try {
    //   if (cosManager.value) {
    //     cosManager.value.cancelTask(cosTaskId)
    //     resetUploadState()
    //   }
    // } catch (error) {
    //   console.error('取消上传失败:', error)
    //   setError('取消上传失败')
    // }
  }
  // 暂停上传
  const pauseUpload = cosTaskId => {
    if (cosManager.value && cosTaskId) {
      cosManager.value.pauseTask(cosTaskId)
    }
  }

  // 恢复上传
  const resumeUpload = cosTaskId => {
    if (cosManager.value && cosTaskId) {
      cosManager.value.restartTask(cosTaskId)
    }
  }

  // 组件卸载时清理资源
  const cleanup = () => {
    if (uploadState.isUploading && uploadState.taskId) {
      cancelUpload()
    }
    cosManager.value = null
  }

  return {
    // 响应式状态
    uploadState: readonly(uploadState),

    // 计算属性
    canUpload,
    hasError,
    isCompleted,

    // 方法
    selectFile: selectAndUploadFile,
    cancelUpload,
    pauseUpload,
    resumeUpload,
    resetUploadState,

    // 工具方法
    getMd5: calculateFileMD5,
    getFileExtension,

    // 清理方法
    cleanup,
  }
}
